import { useEffect } from 'react';

import { store } from 'redux/store';
import { setChartData } from 'redux/reducers/meteorologyFieldsTrackingForecastControlPanelReducer';
import { useLazyGetTrackingForecastChartDataQuery } from 'services/api/Meteorology/Fields/TrackingForecast/DetailedView';
import type { ResponseError } from 'services/api/base';
import type { ChartStatus } from 'types/Meteorology/Fields/TrackingForecast/DetailedView';
import type { GetChartDataParams } from 'types/Meteorology/Fields/TrackingForecast/DetailedView/getChartData';
import { CHART_TYPE_LINE, CHART_TYPE_HEATMAP, type ChartType } from 'types/Meteorology/TrackingForecast';

import useChartDataParams from './useChartDataParams';
import { setChartRequestTrigger, useChartRequestTriggerValue } from './chartRequestTriggerStore';

export default function useChartRequestTrigger() {
  const [baseTrigger] = useLazyGetTrackingForecastChartDataQuery();
  const trigger = useChartRequestTriggerValue();
  const baseParams = useChartDataParams();

  useEffect(() => {
    if (!baseParams) {
      setChartRequestTrigger(undefined);
      return;
    }

    const requestData = async (params: GetChartDataParams) => {
      const { subpage, chartType } = params;
      const status: ChartStatus = { data: undefined, error: undefined, isFetching: true };

      store.dispatch(setChartData([subpage, chartType, { ...status }]));

      const res = await baseTrigger(params);
      status.data = res.data?.data ? res.data.data.option : undefined;
      status.error = res.error as ResponseError;
      status.isFetching = false;

      store.dispatch(setChartData([subpage, chartType, { ...status }]));
    };

    const triggerFunc = async () => {
      const { chartType } = baseParams;
      const secondaryType: ChartType = (chartType === CHART_TYPE_LINE)
        ? CHART_TYPE_HEATMAP
        : CHART_TYPE_LINE;
      const secondaryParams = { ...baseParams, chartType: secondaryType };

      // Em paralelo, não em série: os dois gráficos escrevem em slots distintos de
      // `chartData[subpage][chartType]` e nenhum depende do resultado do outro, então
      // encadear só somava as duas latências. O gráfico visível continua partindo no
      // mesmo instante — quem deixa de esperar é o secundário. Erro de um não afeta o
      // outro: `requestData` captura o erro no próprio slot e não rejeita.
      await Promise.all([
        requestData(baseParams),
        requestData(secondaryParams),
      ]);
    };

    setChartRequestTrigger(triggerFunc);
  }, [baseTrigger, baseParams]);

  return trigger;
}
